import { Fragment, useId, useState } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "../../../../../components/ui/button";
import { SubsectionHeader } from "./SubsectionHeader";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------
type PersonPath = "driver" | "secondSigner";

interface AutoPayInformationProperties {
  personPath: PersonPath;
}

// Placeholder example data — no AutoPay field exists yet in
// @credit-acceptance/step6-bff-client; swap for real hydrated values once a
// BFF field is available.
const AUTO_PAY_EXAMPLE_DATA = [
  ["bankAccountHolder", "JOHN RUBEN"],
  ["accountType", "BANK_ACCOUNT"],
  ["accountStatus", "ACTIVATED"],
  ["bankAccountNumber", "2543"],
  ["bankName", "WELLS FARGO BANK NA"],
  ["bankRoutingNumber", "122000247"],
  ["bankType", "CHECKING"],
  ["periodicPayment", "$405.71"],
  ["debitFrequency", "MONTHLY"],
  ["firstPaymentDueDate", "06/20/2026"],
  ["scheduleDay", "31"],
  ["firstDebitDate", "05/31/2026"],
] as const;

// ---------------------------------------------------------------------------
// AutoPayInformation
//
// Collapsible per-person subsection rendering a static, read-only list of
// AutoPay fields. Unlike BankInformation, there is nothing to bind/validate —
// no AutoPay field exists yet on the BFF client — so this is display-only
// placeholder data until a real field is available.
// ---------------------------------------------------------------------------
export function AutoPayInformation({ personPath }: AutoPayInformationProperties) {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(true);
  // Unique id per mount — prevents aria-controls collisions when both driver
  // and secondSigner columns are rendered in the same document.
  const uid = useId();
  const contentId = `${personPath}-autopay-info-${uid}-content`;

  function handleToggle() {
    setIsOpen((previous) => !previous);
  }

  /**
   * @TEMP: Update both once the endpoint logic is available
   * - Temporary cancel handler for the AutoPay section
   * - IsDisabled is currently set to false as a placeholder.
   */
  function handleCancel() {
    window.alert(t("review.autoPay.cancelAlert"));
  }
  const isDisabled = false; // Placeholder until real logic is available

  return (
    <div>
      <SubsectionHeader
        title={t("review.autoPay.heading")}
        isOpen={isOpen}
        onToggle={handleToggle}
        contentId={contentId}
      />

      {isOpen && (
        <div className="p-(--spacing-3) gap-(--spacing-4) flex flex-col">
          <div
            id={contentId}
            className="text-(length:--typography-base-sizes-extra-small-font-size)/(--typography-base-sizes-extra-small-line-height) gap-(--spacing-3) gap-y-(--spacing-2) grid grid-cols-[max-content_1fr]"
          >
            {AUTO_PAY_EXAMPLE_DATA.map(([key, value]) => (
              <Fragment key={key}>
                <span className="text-muted-foreground font-medium">{t(`review.autoPay.fields.${key}`)}:</span>
                <span className="text-foreground">{value}</span>
              </Fragment>
            ))}
          </div>

          <Button type="button" size="sm" onClick={handleCancel} className="w-fit" disabled={isDisabled}>
            {t("review.autoPay.cancelButton")}
          </Button>
        </div>
      )}
    </div>
  );
}
